"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/lib/store";
import { updateProfile, type Settings } from "@/lib/settings-api";

interface Props {
  settings: Settings | null;
  onUpdate: (s: Settings) => void;
}

export default function ProfileSection({ settings, onUpdate }: Props) {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(settings?.display_name || user?.display_name || "");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) return;
    setName(settings?.display_name || user?.display_name || "");
  }, [editing, settings?.display_name, user?.display_name]);

  const handleSave = async () => {
    setSaveError(null);
    try {
      const updated = await updateProfile({ display_name: name });
      onUpdate(updated);
      // Keep the cached auth identity in sync so the Home greeting updates
      // immediately — not only the Settings header (N3 split-brain fix).
      if (updated.display_name) {
        setUser({ display_name: updated.display_name });
      }
      setEditing(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not save your profile yet.");
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-4" data-testid="settings-profile-section">
      <h2 className="text-sm font-semibold text-text-light mb-3">Profile</h2>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-forest ">
              {(settings?.display_name || user?.display_name || "F")[0].toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div>
              <div className="flex gap-2">
                <input
                  data-testid="settings-profile-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-white bg-bg text-sm text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
                />
                <button
                  type="button"
                  data-testid="settings-profile-save"
                  onClick={handleSave}
                  className="text-xs text-forest font-medium"
                >
                  Save
                </button>
                <button
                  type="button"
                  data-testid="settings-profile-cancel"
                  onClick={() => {
                    setName(settings?.display_name || user?.display_name || "");
                    setSaveError(null);
                    setEditing(false);
                  }}
                  className="text-xs text-muted"
                >
                  Cancel
                </button>
              </div>
              {saveError ? <p className="mt-2 text-xs text-earth">{saveError}</p> : null}
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-text truncate">{settings?.display_name || user?.display_name}</p>
              <p className="text-xs text-muted truncate">{settings?.email || user?.email}</p>
            </>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            data-testid="settings-profile-edit"
            onClick={() => {
              setName(settings?.display_name || user?.display_name || "");
              setSaveError(null);
              setEditing(true);
            }}
            className="text-xs text-forest font-medium"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
